import React, { ReactNode } from 'react';

type EmptyStateVariant = 'default' | 'search' | 'error' | 'success';

interface EmptyStateProps {
  title: string;
  description?: string;
  variant?: EmptyStateVariant;
  icon?: ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

const variantIcons: Record<EmptyStateVariant, ReactNode> = {
  default: (
    <svg
      className="w-12 h-12 text-au-info"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  ),
  search: (
    <svg
      className="w-12 h-12 text-au-info"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  ),
  error: (
    <svg
      className="w-12 h-12 text-au-danger"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  success: (
    <svg
      className="w-12 h-12 text-au-success"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  variant = 'default',
  icon,
  action,
  secondaryAction,
  className = '',
}) => (
  <div className={`flex flex-col items-center justify-center py-12 px-6 text-center ${className}`}>
    {/* Icon */}
    <div className="mb-4">{icon || variantIcons[variant]}</div>

    {/* Title */}
    <h3 className="text-h4 text-au-text-primary mb-2">{title}</h3>

    {/* Description */}
    {description && <p className="text-au-text-secondary max-w-sm mb-6">{description}</p>}

    {/* Actions */}
    {(action || secondaryAction) && (
      <div className="flex flex-col sm:flex-row gap-3">
        {action && (
          <button onClick={action.onClick} className="au-btn au-btn-primary">
            {action.label}
          </button>
        )}
        {secondaryAction && (
          <button onClick={secondaryAction.onClick} className="au-btn au-btn-outline">
            {secondaryAction.label}
          </button>
        )}
      </div>
    )}
  </div>
);

export default EmptyState;
